/**
 * 試合の手札だけを、追従を当てる前の枠で書き出す（Issue #942）。デュエルのページの試合ごとの欄。
 *
 * 席（P1 / P2）ごとに、いちばん新しい kind hand_export のジョブ（GetExportRequests。created_at の新しい順）を見て
 * 「書き出す」→ 待機中・処理中（進み具合。10 秒ごとに読み直す）→「ダウンロード」/ 失敗の理由と「やり直す」。
 * 書き出せる席はフィールド生成の config の手札のエントリ（files と枠があるもの）。無ければ欄を出さない。
 * 処理するのはワーカー（家の Windows・GPU の EC2）。ダウンロードの URL は上げるときに Content-Disposition: attachment。
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/api';
import { OneDriveUploadButton } from './OneDrivePanel';
import { formatServerTime } from '../utils/serverTime';

const KIND = 'hand_export';
const POLL_MS = 10000;

/** config の手札のエントリのうち書き出せる席（サーバーの create_hand_export_request.hand_players と同じ） */
export function handPlayers(config) {
  const out = new Set();
  for (const e of config?.configs ?? []) {
    if (e.field_type === 'hand' && (e.player_number === 1 || e.player_number === 2)
        && e.files?.length && (e.untracked_points || e.points)) {
      out.add(e.player_number);
    }
  }
  return [...out].sort();
}

/** 席ごとのいちばん新しいジョブ（requests は created_at の新しい順） */
export function latestByPlayer(requests) {
  const out = {};
  for (const r of requests ?? []) {
    const p = r.runs?.player_number;
    if (p && !out[p]) out[p] = r;
  }
  return out;
}

const STATUS_TEXT = { pending: '待機中', processing: '処理中', cancelled: 'キャンセル済' };

export default function HandExportPanel({ gameId }) {
  const [players, setPlayers] = useState(null);
  const [latest, setLatest] = useState({});
  const [busy, setBusy] = useState(null);
  const timer = useRef(null);

  const loadRequests = useCallback(async () => {
    clearTimeout(timer.current);
    const rows = await api.getExportRequests({ game_id: gameId, kind: KIND }).catch(() => []);
    const next = latestByPlayer(rows);
    setLatest(next);
    if (Object.values(next).some(r => r.status === 'pending' || r.status === 'processing')) {
      timer.current = setTimeout(loadRequests, POLL_MS);
    }
  }, [gameId]);

  useEffect(() => {
    api.project('GetFieldGenConfig', { game_id: gameId })
      .then(res => setPlayers(handPlayers(res?.config)))
      .catch(() => setPlayers([]));
    loadRequests();
    return () => clearTimeout(timer.current);
  }, [gameId, loadRequests]);

  const start = async (player) => {
    setBusy(player);
    try {
      const res = await api.createHandExportRequest(gameId, player);
      if (res?.error) throw new Error(res.error);
      await loadRequests();
    } catch (err) {
      alert('書き出しを頼めませんでした: ' + err.message);
    } finally {
      setBusy(null);
    }
  };

  if (!players || players.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <h3 style={{ fontSize: '0.85rem', color: '#777', marginBottom: 4 }}>手札（追従前の枠）</h3>
      {players.map(p => {
        const req = latest[p];
        const running = req && (req.status === 'pending' || req.status === 'processing');
        const done = req?.status === 'completed' && req.result_video_url;
        return (
          <div key={p} style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
            padding: '8px 0', borderBottom: '1px solid #1e1e1e',
          }}>
            <span style={{ fontSize: '0.85rem', color: '#777', minWidth: 48 }}>P{p}</span>
            <div style={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}>
              {running && (
                <span style={{ color: '#8ab4f8' }}>{STATUS_TEXT[req.status]}{req.progress ? `: ${req.progress}` : ''}</span>
              )}
              {req?.status === 'failed' && (
                <span style={{ color: '#e89a9a' }}>失敗{req.error_message ? `: ${req.error_message}` : ''}</span>
              )}
              {done && <span style={{ color: '#888' }}>{formatServerTime(req.completed_at)}</span>}
              {!req && <span style={{ color: '#444' }}>まだ書き出していません</span>}
            </div>
            {done && (
              <a className="btn btn-sm btn-primary" href={req.result_video_url} download>ダウンロード</a>
            )}
            {done && <OneDriveUploadButton requestId={req.id} />}
            {!running && (
              <button className="btn btn-sm btn-secondary" onClick={() => start(p)} disabled={busy === p}>
                {busy === p ? '...' : req ? '書き出し直す' : '書き出す'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
